import {
  DotsThree,
  FlowArrow,
  Link as LinkIcon,
  LinkSimple,
  SlidersHorizontal,
  TextT,
  UploadSimple,
} from '@phosphor-icons/react';
import {
  Avatar,
  Banner,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  cn,
  EmptyState,
  Field,
  IconButton,
  Inline,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Modal,
  ModalClose,
  ModalContent,
  Stack,
  TextInput,
  toast,
} from '@reelplicate/ui';
import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';

import { CompetitorPostCard } from '@/features/competitors/competitor-post-card';
import { usePostActions } from '@/features/competitors/use-post-actions';

import { AssetBrowser } from './asset-browser';
import { collectionItems } from './library';
import { toAsset } from './media-api';
import { useMedia } from './media-context';
import { ShowLibraryButton } from './media-layout';
import { useMediaOutlet } from './media-outlet';
import type { Collection } from './types';
import { useCollectionEntries } from './use-collection-entries';

const soon = (what: string) => () => toast(`${what} is coming soon`);

/** Figma "Cover": three 56×96 covers fanned out (-6°, 0°, 6°). */
function FannedCovers({ covers }: { covers: string[] }) {
  const tilt = ['rotate-6', '', '-rotate-6'];
  return (
    <div aria-hidden className="flex shrink-0 items-center py-[3px]">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            'border-bg-canvas bg-bg-emphasis h-24 w-14 overflow-hidden rounded-[10px] border-3',
            i < 2 && '-mr-[22px]',
            i === 1 && 'relative z-10',
            tilt[i],
          )}
        >
          {covers[i] && <img src={covers[i]} alt="" className="size-full object-cover" />}
        </div>
      ))}
    </div>
  );
}

/**
 * Figma "Screen — Media · Collection" (190:1809): curate, share and use a
 * collection as a flow source. Hand-picked collections suggest turning
 * smart; smart ones show their rule.
 */
export function CollectionPage() {
  const { collectionId } = useParams();
  const {
    loading,
    assets,
    owners,
    collections,
    workspaceId,
    createCollection,
    copyCollection,
    deleteCollection,
  } = useMedia();
  const navigate = useNavigate();
  const collection = collections.find((c) => c.id === collectionId);
  const { newCaption, addFromLink, pickFiles, editSmartCollection } = useMediaOutlet();
  const [renaming, setRenaming] = useState(false);
  // A smart collection's matches come from the server, in its order.
  const entries = useCollectionEntries(
    workspaceId,
    collection?.id,
    Boolean(collection?.smart),
  );
  const matches = useMemo(
    () =>
      entries.data?.flatMap((entry) =>
        entry.asset ? [toAsset(entry.asset, owners)] : [],
      ) ?? [],
    [entries.data, owners],
  );
  // Tracked competitors' posts a smart rule matches that aren't saved yet.
  const feedPosts = useMemo(
    () => entries.data?.flatMap((entry) => (entry.post ? [entry.post] : [])) ?? [],
    [entries.data],
  );
  const postActions = usePostActions(workspaceId);

  const [now] = useState(() => Date.now());

  if (loading || (collection?.smart && entries.isPending)) return null;

  if (!collection) {
    return (
      <EmptyState
        icon={SlidersHorizontal}
        title="Collection not found"
        description="It may have been deleted."
        actions={
          <Button asChild variant="outline">
            <RouterLink to="/media">Back to Media</RouterLink>
          </Button>
        }
        className="mx-auto"
      />
    );
  }

  const smart = collection.smart;
  const items = smart ? matches : collectionItems(collection, assets);
  const covers = items.flatMap((a) => (a.thumbnail ? [a.thumbnail] : []));
  const people = collection.sharedWith ?? [];
  const editRules = () => editSmartCollection({ collection });
  const total = items.length + feedPosts.length;
  const meta = [
    `${total} ${total === 1 ? 'item' : 'items'}`,
    smart && `smart · ${smart.rule}`,
    people.length > 0 && `shared with ${people.length} people`,
    collection.updated,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Breadcrumb>
        <BreadcrumbItem asChild>
          <RouterLink to="/media">Media</RouterLink>
        </BreadcrumbItem>
        <BreadcrumbItem asChild>
          <RouterLink to="/media">Collections</RouterLink>
        </BreadcrumbItem>
        <BreadcrumbItem current>{collection.name}</BreadcrumbItem>
      </Breadcrumb>

      <Stack as="header" gap={5}>
        <Inline gap={5}>
          <ShowLibraryButton />
          <FannedCovers covers={covers} />
          <Stack gap={1.5} className="min-w-0 flex-1">
            <h1 className="text-heading-h1 text-text-primary">{collection.name}</h1>
            {collection.description && (
              <p className="text-body-md text-text-secondary">{collection.description}</p>
            )}
            <div className="flex items-center gap-2.5">
              {people.length > 0 && (
                <span className="flex">
                  {people.map((initials, i) => (
                    <Avatar
                      key={initials}
                      size={24}
                      initials={initials}
                      alt=""
                      className={i < people.length - 1 ? '-mr-1.5' : undefined}
                    />
                  ))}
                </span>
              )}
              <p className="text-caption text-text-secondary">{meta}</p>
            </div>
          </Stack>
        </Inline>
        <Inline gap={2} wrap>
          <Menu>
            <MenuTrigger asChild>
              <IconButton
                icon={DotsThree}
                variant="outline"
                aria-label="Collection actions"
              />
            </MenuTrigger>
            <MenuContent align="end">
              <MenuItem onSelect={() => setRenaming(true)}>Rename</MenuItem>
              <MenuItem
                onSelect={async () => {
                  const copy = await createCollection(
                    `${collection.name} (copy)`.slice(0, 60),
                    smart ? { rule: smart } : { assetIds: collection.assetIds ?? [] },
                  );
                  if (!copy) return;
                  toast.success(`Duplicated as “${copy.name}”`);
                  void navigate(`/media/collections/${copy.id}`);
                }}
              >
                Duplicate
              </MenuItem>
              {smart && (
                <MenuItem
                  onSelect={async () => {
                    const copy = await copyCollection(collection.id);
                    if (!copy) return;
                    toast.success(`Saved today’s ${copy.count} as “${copy.name}”`);
                    void navigate(`/media/collections/${copy.id}`);
                  }}
                >
                  Save a copy as hand-picked
                </MenuItem>
              )}
              <MenuItem
                type="danger"
                onSelect={async () => {
                  if (!(await deleteCollection(collection.id))) return;
                  toast.success(`Deleted “${collection.name}”`);
                  void navigate('/media');
                }}
              >
                Delete collection
              </MenuItem>
            </MenuContent>
          </Menu>
          {!smart && (
            <>
              <Button
                variant="outline"
                leadingIcon={TextT}
                onClick={() => newCaption({ collectionId: collection.id })}
              >
                Add caption
              </Button>
              <Button
                variant="outline"
                leadingIcon={LinkSimple}
                onClick={() => addFromLink(collection.id)}
              >
                Add from link
              </Button>
              <Button
                variant="outline"
                leadingIcon={UploadSimple}
                onClick={() => pickFiles(collection.id)}
              >
                Upload
              </Button>
            </>
          )}
          {smart ? (
            <Button variant="outline" leadingIcon={SlidersHorizontal} onClick={editRules}>
              Edit rules
            </Button>
          ) : (
            <Button variant="outline" leadingIcon={LinkIcon} onClick={soon('Sharing')}>
              Share
            </Button>
          )}
          <Button leadingIcon={FlowArrow} onClick={soon('Flows')}>
            Use as flow source
          </Button>
        </Inline>
      </Stack>

      {smart && smart.broken.length > 0 && (
        <Banner
          tone="danger"
          title="This rule points to something that was deleted"
          description="A persona or competitor in it no longer exists, so that condition matches nothing. Flows using this collection get nothing from it until you fix the rule."
          action={{ label: 'Edit rules', onClick: editRules }}
        />
      )}

      <AssetBrowser
        key={collection.id}
        assets={items}
        notice={
          <div className="border-border-subtle flex items-center gap-2.5 rounded-[10px] border border-dashed px-3.5 py-2.5">
            <SlidersHorizontal
              aria-hidden
              className="text-icon-secondary size-4 shrink-0"
            />
            <p className="text-body-sm text-text-secondary flex-1">
              {smart
                ? `Smart collection: anything that ${smart.rule.startsWith('matches') ? smart.rule : `has ${smart.rule}`} is added automatically.`
                : 'Hand-picked collection. For one that fills itself from a rule, make a smart collection.'}
            </p>
            <button
              type="button"
              onClick={smart ? editRules : () => editSmartCollection()}
              className="text-body-sm-strong text-text-link focus-visible:shadow-focus-ring rounded-xs px-0.5 outline-none hover:underline"
            >
              {smart ? 'Edit rules' : 'New smart collection'}
            </button>
          </div>
        }
        empty={
          smart ? (
            <EmptyState
              icon={SlidersHorizontal}
              title="Nothing matches this rule"
              description={
                smart.broken.length
                  ? 'Pick another value for the broken condition, or remove it. Matching items show up here on their own.'
                  : 'New items that match show up here on their own. Loosen the rule to include more.'
              }
              actions={
                <Button
                  variant="outline"
                  leadingIcon={SlidersHorizontal}
                  onClick={editRules}
                >
                  Edit rules
                </Button>
              }
              className="mx-auto"
            />
          ) : undefined
        }
      />
      {feedPosts.length > 0 && (
        <section aria-labelledby="feed-posts" className="flex flex-col gap-3">
          <div className="flex flex-col gap-0.5">
            <h2 id="feed-posts" className="text-heading-h4 text-text-primary">
              From competitors · not saved yet
            </h2>
            <p className="text-body-sm text-text-secondary">
              {feedPosts.length} {feedPosts.length === 1 ? 'post matches' : 'posts match'}{' '}
              this rule. Save one to keep it, or make your version.
            </p>
          </div>
          <ul
            aria-label="Competitor posts"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {feedPosts.map((post) => (
              <CompetitorPostCard
                key={post.id}
                post={post}
                now={now}
                busy={postActions.busyOn(post.id)}
                onSave={() => postActions.save(post)}
                onMakeMyVersion={() => postActions.remake(post)}
              />
            ))}
          </ul>
        </section>
      )}
      <RenameModal
        collection={renaming ? collection : null}
        onClose={() => setRenaming(false)}
      />
    </>
  );
}

function RenameModal({
  collection,
  onClose,
}: {
  collection: Collection | null;
  onClose: () => void;
}) {
  const { updateCollection } = useMedia();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [openFor, setOpenFor] = useState<string | null>(null);
  if (collection && openFor !== collection.id) {
    setOpenFor(collection.id);
    setName(collection.name);
  }
  if (!collection && openFor !== null) setOpenFor(null);

  const save = async () => {
    if (!collection || !name.trim()) return;
    setSaving(true);
    const saved = await updateCollection(collection.id, { name: name.trim() });
    setSaving(false);
    if (saved) onClose();
  };

  return (
    <Modal open={collection !== null} onOpenChange={(open) => !open && onClose()}>
      <ModalContent
        size="sm"
        title="Rename collection"
        footer={
          <>
            <ModalClose asChild>
              <Button variant="ghost">Cancel</Button>
            </ModalClose>
            <Button disabled={!name.trim()} loading={saving} onClick={() => void save()}>
              Rename
            </Button>
          </>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Field label="Name">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              autoComplete="off"
            />
          </Field>
        </form>
      </ModalContent>
    </Modal>
  );
}
